<div class="modal-header d-flex justify-content-between align-items-start">
  <div class="modal-title">
    <h3>Lagerstatus</h3>
    <p class="mb-0">Här ser du aktuella lagerartiklar och lagersaldon. Du kan filtrera och söka bland artiklarna.</p>
  </div>
  <button type="button" class="btn-close" (click)="close()" aria-label="Stäng"></button>
</div>

<div class="modal-body">
  @if (errorMessage()) {
    <p-message severity="error">{{ errorMessage() }}</p-message>
  } @else if (isLoading()) {
    <p class="mb-0 text-muted" aria-live="polite">Hämtar lagersaldo...</p>
  } @else {
    <div class="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-2">
      <div class="flex-fill">
        <div class="inventory-summary h-100 border rounded p-3 d-flex align-items-center gap-3">
          <p-avatar size="normal" shape="circle" class="inventory-balance-total-avatar flex-shrink-0">
            <i class="fa-regular fa-cube" aria-hidden="true"></i>
          </p-avatar>
          <div class="min-w-0">
            <div class="small text-body-secondary">Totalt antal lagerartiklar</div>
            <div class="inventory-summary-value">{{ inventoryBalances().length | number: '1.0-0' : 'sv-SE' }}</div>
          </div>
        </div>
      </div>

      <div class="flex-fill">
        <div class="inventory-summary h-100 border rounded p-3 d-flex align-items-center gap-3">
          <p-avatar size="normal" shape="circle" class="inventory-balance-high-avatar flex-shrink-0">
            <i class="fa-solid fa-circle-small" aria-hidden="true"></i>
          </p-avatar>
          <div class="min-w-0">
            <div class="small text-body-secondary">Artiklar med 15+ i lager</div>
            <div class="inventory-summary-value">{{ veryHighBalance() | number: '1.0-0' : 'sv-SE' }}</div>
          </div>
        </div>
      </div>

      <div class="flex-fill">
        <div class="inventory-summary h-100 border rounded p-3 d-flex align-items-center gap-3">
          <p-avatar size="normal" shape="circle" class="inventory-balance-total-avatar flex-shrink-0">
            <i class="fa-solid fa-circle-small" aria-hidden="true"></i>
          </p-avatar>
          <div class="min-w-0">
            <div class="small text-body-secondary">Artiklar med 11-15 i lager</div>
            <div class="inventory-summary-value">{{ highBalance() | number: '1.0-0' : 'sv-SE' }}</div>
          </div>
        </div>
      </div>

      <div class="flex-fill">
        <div class="inventory-summary h-100 border rounded p-3 d-flex align-items-center gap-3">
          <p-avatar size="normal" shape="circle" class="inventory-balance-medium-avatar flex-shrink-0">
            <i class="fa-solid fa-circle-small" aria-hidden="true"></i>
          </p-avatar>
          <div class="min-w-0">
            <div class="small text-body-secondary">Artiklar med 6-10 i lager</div>
            <div class="inventory-summary-value">{{ mediumBalance() | number: '1.0-0' : 'sv-SE' }}</div>
          </div>
        </div>
      </div>

      <div class="flex-fill">
        <div class="inventory-summary h-100 border rounded p-3 d-flex align-items-center gap-3">
          <p-avatar size="normal" shape="circle" class="inventory-balance-low-avatar flex-shrink-0">
            <i class="fa-solid fa-circle-small" aria-hidden="true"></i>
          </p-avatar>
          <div class="min-w-0">
            <div class="small text-body-secondary">Artiklar med 0-5 i lager</div>
            <div class="inventory-summary-value">{{ lowBalance() | number: '1.0-0' : 'sv-SE' }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex flex-column gap-3 mt-4">
      <div id="search-options" class="row g-2 align-items-center">
        <div class="col-12 col-lg-4 col-xl-3">
          <p-floatlabel variant="on" class="w-100">
            <p-iconfield class="w-100">
              <p-inputicon class="fa-duotone fa-magnifying-glass" />
              <input
                pInputText
                #searchInput
                id="inventory-search"
                class="w-100"
                type="search"
                pSize="small"
                autocomplete="off"
                enterkeyhint="search"
                [value]="searchTerm()"
                (input)="updateSearch(searchInput.value)"
                (keydown.escape)="clearFilters()"
              />
              <label for="inventory-search">Sök artikel eller artikelnummer</label>
            </p-iconfield>
          </p-floatlabel>
        </div>

        <div class="col-12 col-sm-6 col-lg-4 col-xl">
          <p-floatlabel variant="on" class="w-100">
            <p-select
              inputId="inventory-material"
              class="w-100"
              appendTo="body"
              size="small"
              [fluid]="true"
              [options]="materialOptions()"
              [ngModel]="selectedMaterial()"
              [showClear]="true"
              [checkmark]="true"
              (ngModelChange)="updateMaterial($event)"
            />
            <label for="inventory-material">Material</label>
          </p-floatlabel>
        </div>

        <div class="col-12 col-sm-6 col-lg-4 col-xl">
          <p-floatlabel variant="on" class="w-100">
            <p-select
              inputId="inventory-product-type"
              class="w-100"
              appendTo="body"
              size="small"
              [fluid]="true"
              [options]="productTypeOptions()"
              [ngModel]="selectedProductType()"
              [showClear]="true"
              [checkmark]="true"
              (ngModelChange)="updateProductType($event)"
            />
            <label for="inventory-product-type">Produkttyp</label>
          </p-floatlabel>
        </div>

        <div class="col-12 col-sm-6 col-lg-4 col-xl">
          <p-floatlabel variant="on" class="w-100">
            <p-select
              inputId="inventory-profile"
              class="w-100"
              appendTo="body"
              size="small"
              [fluid]="true"
              [options]="profileOptions()"
              [ngModel]="selectedProfile()"
              [showClear]="true"
              [checkmark]="true"
              (ngModelChange)="updateProfile($event)"
            />
            <label for="inventory-profile">Profil</label>
          </p-floatlabel>
        </div>

        <div class="col-12 col-sm-6 col-lg-4 col-xl">
          <p-floatlabel variant="on" class="w-100">
            <p-select
              inputId="inventory-function"
              class="w-100"
              appendTo="body"
              size="small"
              [fluid]="true"
              [options]="functionOptions()"
              [ngModel]="selectedFunction()"
              [showClear]="true"
              [checkmark]="true"
              (ngModelChange)="updateFunction($event)"
            />
            <label for="inventory-function">Utförande</label>
          </p-floatlabel>
        </div>

        <div class="col-12 col-sm-6 col-lg-4 col-xl-auto">
          <button
            type="button"
            class="btn btn-sm btn-link inventory-clear-filter"
            [disabled]="!hasActiveFilters()"
            (click)="clearFilters()"
          >
            Rensa filter
          </button>
        </div>
      </div>

      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 small text-body-secondary">
        <span aria-live="polite">
          Visar {{ filteredInventoryBalances().length | number: '1.0-0' : 'sv-SE' }} av
          {{ inventoryBalances().length | number: '1.0-0' : 'sv-SE' }} artiklar
        </span>
        @if (hasActiveFilters()) {
          <span>Filtrerat resultat</span>
        }
      </div>

      <div
        class="inventory-table-wrapper"
        role="region"
        aria-label="Lagerartiklar. Tabellen kan rullas i sidled på små skärmar."
        tabindex="0">

        <p-table
          class="p-datatable-sm"
          dataKey="itemId"
          sortField="itemId"
          [value]="filteredInventoryBalances()"
          [sortOrder]="1"
          [rowHover]="true"
          [showGridlines]="true"
          [paginator]="true"
          [alwaysShowPaginator]="filteredInventoryBalances().length > 0"
          [first]="first()"
          [rows]="rows()"
          [rowsPerPageOptions]="rowsPerPageOptions"
          [pageLinks]="5"
          [showFirstLastIcon]="true"
          [showCurrentPageReport]="true"
          paginatorDropdownAppendTo="body"
          currentPageReportTemplate="{first}-{last} av {totalRecords}"
          paginatorStyleClass="inventory-paginator"
          [tableStyle]="{ 'min-width': '76rem' }"
          (onPage)="onPage($event)">

          <ng-template #header>
            <tr>
              <th pSortableColumn="material">
                Material
                <p-sortIcon field="material" />
              </th>
              <th pSortableColumn="type">
                Produkttyp
                <p-sortIcon field="type" />
              </th>
              <th pSortableColumn="profile">
                Profil
                <p-sortIcon field="profile" />
              </th>
              <th pSortableColumn="function">
                Utförande
                <p-sortIcon field="function" />
              </th>
              <th class="inventory-article-name" pSortableColumn="itemName">
                Artikel
                <p-sortIcon field="itemName" />
              </th>
              <th class="inventory-item-id" pSortableColumn="itemId">
                Artikelnummer
                <p-sortIcon field="itemId" />
              </th>
              <th class="inventory-stock" pSortableColumn="balance">
                Lagerstatus
                <p-sortIcon field="balance" />
              </th>
            </tr>
          </ng-template>

          <ng-template #body let-item>
            <tr>
              <td>{{ item.material }}</td>
              <td>{{ item.type }}</td>
              <td>{{ item.profile }}</td>
              <td>{{ item.function }}</td>
              <td class="inventory-article-name">
                <span [title]="item.itemName">{{ item.itemName }}</span>
              </td>
              <td class="inventory-item-id">{{ item.itemId }}</td>
              <td class="inventory-stock">
                @if (item.balance < 6) {
                  <span class="inventory-stock-value text-danger" [attr.aria-label]="'Låg lagerstatus, ' + item.balanceLevel + ' i lager'">
                    <i class="fa-duotone fa-battery-low" aria-hidden="true"></i>
                    <span>{{ item.balanceLevel }}</span>
                  </span>
                } @else if (item.balance < 11) {
                  <span class="inventory-stock-value text-warning" [attr.aria-label]="'Begränsad lagerstatus, ' + item.balanceLevel + ' i lager'">
                    <i class="fa-duotone fa-battery-half" aria-hidden="true"></i>
                    <span>{{ item.balanceLevel }}</span>
                  </span>
                } @else if (item.balance < 16) {
                  <span class="inventory-stock-value text-primary" [attr.aria-label]="'God lagerstatus, ' + item.balanceLevel + ' i lager'">
                    <i class="fa-duotone fa-battery-three-quarters" aria-hidden="true"></i>
                    <span>{{ item.balanceLevel }}</span>
                  </span>
                } @else {
                  <span class="inventory-stock-value text-success" [attr.aria-label]="'Hög lagerstatus, ' + item.balanceLevel + ' i lager'">
                    <i class="fa-duotone fa-battery-full" aria-hidden="true"></i>
                    <span>{{ item.balanceLevel }}</span>
                  </span>
                }
              </td>
            </tr>
          </ng-template>

          <ng-template #emptymessage>
            <tr>
              <td colspan="7" class="py-4 text-center">
                @if (hasActiveFilters()) {
                  <p class="mb-2">Inga artiklar matchar de valda filtren.</p>
                  <button type="button" class="btn btn-sm btn-outline-primary" (click)="clearFilters()">
                    Rensa filter
                  </button>
                } @else {
                  <span>Det finns inga lagerartiklar att visa.</span>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>
  }
</div>

<div class="modal-footer">
  <button type="button" class="btn btn-sm btn-primary" (click)="close()">Stäng</button>
</div>
